import React, { createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import RegistrationDialog from "./RegistrationDialog/RegistrationDialog";
import RegistrationCompareMatchDialog from "./RegistrationCompareMatchDialog/RegistrationCompareMatchDialog";

export const AdminContext = createContext();
const SESSION_STORAGE_REGISTRATION_KEY = "ADOPTION_SELECTED_REGISTRATION";

export function AdminContextProvider({ children }) {
  const location = useLocation();

  const [selectedRegistration, setSelectedRegistrationState] = React.useState(
    JSON.parse(sessionStorage.getItem(SESSION_STORAGE_REGISTRATION_KEY))
  );

  const [syncValue, setSyncValue] = React.useState(0);
  const [matchRecord, setMatchRecord] = React.useState();
  const [dialogUserFriendlyRegistrationId, setDialogUserFriendlyRegistrationId] = React.useState();
  const [dialogSectionToOpen, setDialogSectionToOpen] = React.useState();

  function setSelectedRegistration(registration) {
    sessionStorage.setItem(SESSION_STORAGE_REGISTRATION_KEY, JSON.stringify(registration));
    setSelectedRegistrationState(registration);
  }

  function openDialogForUserFriendlyRegistrationId(userFriendlyId, sectionToOpen) {
    // If both match dialog and registration dialog are open, close match dialog instead of trying to "re-open" registration dialog
    if (matchRecord && dialogUserFriendlyRegistrationId) {
      setMatchRecord(null);
    }
    setDialogUserFriendlyRegistrationId(userFriendlyId);
    setDialogSectionToOpen(sectionToOpen);
  }

  function openIdentityForUserFriendlyRegistrationId(userFriendlyId) {
    openDialogForUserFriendlyRegistrationId(userFriendlyId, "identity");
  }

  function openDetailsForUserFriendlyRegistrationId(userFriendlyId) {
    openDialogForUserFriendlyRegistrationId(userFriendlyId, "details");
  }

  function openPaymentForUserFriendlyRegistrationId(userFriendlyId) {
    openDialogForUserFriendlyRegistrationId(userFriendlyId, "payment");
  }

  function openMatchDialog(match) {
    // If both match dialog and registration dialog are open, close registration dialog instead of trying to "re-open" match dialog
    if (matchRecord && dialogUserFriendlyRegistrationId) {
      setDialogUserFriendlyRegistrationId(null);
      setDialogSectionToOpen(null);
    }
    setMatchRecord(match);
  }

  function openDetailsForComparingRegistrations(registration1, registration2) {
    // Populate a dummy "match" record so it can be handled like a match
    openMatchDialog({
      matchId: null,
      adoptee: registration1,
      relative: registration2,
    });
  }

  React.useEffect(() => {
    if (location.pathname !== "/admin/search") {
      setSelectedRegistration(null);
    }
  }, [location]);

  /**
   * Call this function when changes are made to data that could cause the data in tables to be stale
   */
  function triggerDataResync() {
    // By incrementing the value, any component that "watches" this value in a `useEffect` block
    // will automatically re-run the `useEffect` function.
    setSyncValue(syncValue + 1);
  }

  const value = {
    selectedRegistration,
    setSelectedRegistration,
    setMatchRecord,
    openIdentityForUserFriendlyRegistrationId,
    openDetailsForUserFriendlyRegistrationId,
    openPaymentForUserFriendlyRegistrationId,
    openDetailsForComparingRegistrations,
    openMatchDialog,
    triggerDataResync,
    dataResyncFlag: syncValue,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
      <RegistrationDialog
        open={Boolean(dialogUserFriendlyRegistrationId)}
        onClose={() => {
          setDialogUserFriendlyRegistrationId(null);
          setDialogSectionToOpen(null);
        }}
        userFriendlyRegistrationId={dialogUserFriendlyRegistrationId}
        sectionToOpen={dialogSectionToOpen}
      />
      <RegistrationCompareMatchDialog
        open={Boolean(matchRecord)}
        onClose={() => {
          setMatchRecord(null);
        }}
        matchRecord={matchRecord}
      />
    </AdminContext.Provider>
  );
}

// Define a custom "hook" to get the admin context
const useAdminContext = () => useContext(AdminContext);
export default useAdminContext;
